// Command palette (⌘K / Ctrl+K, or "/"): jump to a page, an engagement, a workpaper or a folder, or run an allowed catalogue
// command through its schema form. Commands the person may not run are listed with the reason, never hidden silently.
// Mounted only while open, so every opening starts from an empty query.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal, SchemaForm, inputCls } from "../components";
import type { CommandSpec, SearchEntry } from "../data/types";
import { mayRequest } from "../lib/commands";
import { useLoad } from "../lib/hooks";
import { useApp } from "./context";
import { useEngine } from "./engine";
import { ENGAGEMENT_SECTIONS, ENGAGEMENT_TABS, RAIL } from "./nav";

interface Entry { id: string; group: "Pages" | "Engagements" | "Workpapers" | "Folders" | "Commands"; label: string; detail: string; href?: string; spec?: CommandSpec; disabled?: string | null }

const GROUP: Record<SearchEntry["kind"], Entry["group"]> = { engagement: "Engagements", workpaper: "Workpapers", folder: "Folders", page: "Pages" };

export function currentEngagement(pathname: string): string | null {
  const m = pathname.match(/^\/(?:e|engagements)\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { ds, me, catalogue } = useApp();
  const request = useEngine();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const here = currentEngagement(pathname);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [cmd, setCmd] = useState<CommandSpec | null>(null);
  const [eng, setEng] = useState<string | null>(here);
  const index = useLoad(() => (open ? ds.searchIndex() : Promise.resolve([] as SearchEntry[])), [ds, open]);
  const engagements = useLoad(() => (open ? ds.engagements() : Promise.resolve([])), [ds, open]);
  const input = useRef<HTMLInputElement>(null);
  const formArea = useRef<HTMLDivElement>(null);
  // Moving from the list to a command's form: put focus in the form, not on the page behind it.
  useEffect(() => { if (cmd) formArea.current?.querySelector<HTMLElement>("select, input, textarea, button[type=submit]")?.focus(); }, [cmd]);


  const entries = useMemo<Entry[]>(() => {
    const pages: Entry[] = RAIL.map((r) => ({ id: "p:" + r.to, group: "Pages", label: r.label, detail: r.keywords ?? "", href: r.to }));
    if (here) for (const t of ENGAGEMENT_SECTIONS) pages.push({ id: "s:" + t.path, group: "Pages", label: `Engagement · ${t.label}`, detail: here, href: `/e/${here}${t.path ? "/" + t.path : ""}` });
    if (here) for (const t of ENGAGEMENT_TABS) pages.push({ id: "t:" + t.path, group: "Pages", label: `Engine details · ${t.label}`, detail: here, href: `/e/${here}/details${t.path ? "/" + t.path : ""}` });
    const found: Entry[] = (index.data ?? []).map((s, i) => ({ id: `s:${i}`, group: GROUP[s.kind], label: s.label, detail: s.detail, href: s.href }));
    const cmds: Entry[] = [...catalogue.values()].map((c) => {
      const a = mayRequest(c, me, c.scope);
      return { id: "c:" + c.name, group: "Commands", label: c.name, detail: c.summary, spec: c, disabled: a.ok ? null : a.reason };
    });
    const all = [...pages, ...found, ...cmds];
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    const hit = (e: Entry) => terms.every((t) => `${e.label} ${e.detail} ${e.group}`.toLowerCase().includes(t));
    return (terms.length ? all.filter(hit) : all.filter((e) => e.group === "Pages" || e.group === "Engagements")).slice(0, 60);
  }, [index.data, catalogue, me, q, here]);

  const choose = (e: Entry | undefined) => {
    if (!e || e.disabled) return;
    if (e.spec) { setCmd(e.spec); return; }
    if (e.href) { onClose(); nav(e.href); }
  };

  return (
    <Modal open={open} onClose={onClose} label="Command palette">
      {!cmd ? (
        <>
          <div className="border-b border-line p-2">
            <input ref={input} data-autofocus role="combobox" aria-expanded="true" aria-controls="palette-list" aria-autocomplete="list"
              aria-activedescendant={entries[active] ? `pal-${entries[active].id}` : undefined} aria-label="Search pages, engagements, workpapers, folders and commands"
              placeholder="Jump to… or run a command" className={`${inputCls} border-0 text-base focus-visible:outline-none`} value={q}
              onChange={(e) => { setQ(e.target.value); setActive(0); }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, entries.length - 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
                else if (e.key === "Enter") { e.preventDefault(); choose(entries[active]); }
              }} />
          </div>
          <ul id="palette-list" role="listbox" aria-label="Results" className="max-h-[55vh] overflow-y-auto p-1">
            {entries.length === 0 && <li className="px-3 py-6 text-center text-sm text-muted">{index.loading ? "Loading…" : "Nothing matches."}</li>}
            {entries.map((e, i) => (
              <li key={e.id} id={`pal-${e.id}`} role="option" aria-selected={i === active} aria-disabled={!!e.disabled || undefined}
                onMouseEnter={() => setActive(i)} onClick={() => choose(e)}
                className={`flex cursor-pointer items-center gap-3 rounded px-3 py-1.5 text-sm ${i === active ? "bg-brand/10" : ""} ${e.disabled ? "opacity-60" : ""}`}>
                <span className="w-20 shrink-0 text-[11px] uppercase tracking-wide text-muted">{e.group}</span>
                <span className={`min-w-0 flex-1 truncate ${e.spec ? "font-mono" : ""}`}>{e.label}</span>
                <span className="hidden max-w-[45%] truncate text-xs text-muted sm:inline">{e.disabled ?? e.detail}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div ref={formArea} className="overflow-y-auto p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div><p className="font-mono text-sm font-semibold">{cmd.name}</p><p className="text-sm text-muted">{cmd.summary}</p></div>
            <Button size="sm" onClick={() => setCmd(null)}>Back</Button>
          </div>
          {cmd.scope === "engagement" && (
            <div className="mb-3 space-y-1">
              <label htmlFor="pal-eng" className="block text-sm font-medium">Engagement</label>
              <select id="pal-eng" className={inputCls} value={eng ?? ""} onChange={(e) => setEng(e.target.value || null)}>
                <option value="">Choose…</option>
                {(engagements.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.clientName} · {e.fye}</option>)}
              </select>
            </div>
          )}
          <SchemaForm spec={cmd} personName={me.personName} onCancel={() => setCmd(null)}
            onSubmit={async (payload) => {
              if (cmd.scope === "engagement" && !eng) return;
              const row = await request({ command: cmd.name, engagementId: cmd.scope === "engagement" ? eng : null, payload, what: cmd.summary });
              if (row) onClose();
            }} />
          {cmd.scope === "engagement" && !eng && <p className="mt-2 text-xs text-blocked">Choose the engagement first.</p>}
        </div>
      )}
    </Modal>
  );
}
